import React, { useState, useEffect } from 'react';

import { useTheme } from 'styled-components';

import { FaFileExcel } from 'react-icons/fa';
import { FaFilePdf } from 'react-icons/fa';
import { IoMdArrowDropup, IoMdArrowDropdown } from 'react-icons/io';
import { FaCalendarAlt } from 'react-icons/fa';

import api from '~/services/api';

import { exportCsv } from '~/helpers/csv';
import { exportPdf } from '~/helpers/pdf';

import {
  Scroll,
  Container,
  Header,
  Progress,
  Loading,
  Grid,
  GridLine,
  GridHeadItem,
  GridItem,
} from '~/components';

import { GraphArea, LoadArea, ToogleInfo } from './styles';

import Grafico from './Grafico';

import RelatorioVendaAnual from '~/@types/relatoriovendaanual';

const VendasAnuais: React.FC = () => {
  const { colors } = useTheme();

  const [inLoad, setInLoad] = useState(false);

  // VENDAS Anuais
  const [anos, setAnos] = useState<RelatorioVendaAnual[]>([]);
  const [anosVisibilidade, setGraficoVisibilidade] = useState(true);

  const gerarRelatorioGraficoCSV = () => {
    let data: string[] = [];

    data.push('ANO;PEDIDOS;TICKETMEDIO;PRODUTOS;FRETE;DESCONTO;TOTAL');

    let c;
    for (c in anos) {
      const ano = anos[c];
      data.push(
        `${ano.ano};${ano.pedidos};${ano.ticketmedio};${ano.produtos};${ano.frete};${ano.desconto};${ano.total}`,
      );
    }

    const csv = data.join('\n');

    exportCsv(csv, 'VENDAS ANUAIS');
  };

  const gerarRelatoriosGraficoPDF = () => {
    const cabecalho = [
      'ANO',
      'PEDIDOS',
      'TICKET MÉDIO',
      'PRODUTOS',
      'FRETE',
      'DESCONTO',
      'TOTAL',
    ];

    const data: string[][] = [];

    let c;
    for (c in anos) {
      const ano = anos[c];

      const linha: string[] = [];

      linha.push(`${ano.ano}`);
      linha.push(`${ano.pedidos}`);
      linha.push(`${ano.ticketmedio}`);
      linha.push(`${ano.produtos}`);
      linha.push(`${ano.frete}`);
      linha.push(`${ano.desconto}`);
      linha.push(`${ano.total}`);

      data.push(linha);
    }

    exportPdf(cabecalho, data, 'VENDAS ANUAIS');
  };

  const exibicaoTotalPedidos = (pTotalPedidos: number) => {
    if (!pTotalPedidos) return 'Nenhum pedido';
    if (pTotalPedidos === 1) {
      return 'Um pedido';
    }
    return `${pTotalPedidos} pedidos`;
  };

  useEffect(() => {
    const fetchVendasAnuais = async () => {
      try {
        setInLoad(true);
        setAnos([]);

        const ano = new Date().getFullYear();
        const modulo = 'vendas_por_ano';

        const response = await api.get(
          `relatorios?ano=${ano}&modulo=${modulo}`,
        );

        const { data } = response;

        if (data && Array.isArray(data) && data.length) {
          setAnos(data);
        }

        setInLoad(false);
      } catch (e) {
        setInLoad(false);
      }
    };

    fetchVendasAnuais();
  }, []);

  return (
    <>
      {inLoad ? <Progress /> : null}

      <Scroll>
        <Container>
          <Header
            title="Vendas Anuais"
            subtitle="Confira as métricas de vendas distribuídas mensalmente"
            icon={
              <>
                <FaCalendarAlt size={32} color={colors.IC1} />
              </>
            }
          />

          {inLoad ? (
            <LoadArea>
              <Loading color={colors.IC1} size={42} />
            </LoadArea>
          ) : null}

          {anos.length ? (
            <GraphArea>
              <header>
                <i>
                  <FaCalendarAlt size={22} color={colors.IC1} />
                </i>
                <p>
                  <h1>Vendas Anuais</h1>
                  <h2>
                    Confira as métricas de vendas distribuídas mensalmente
                  </h2>
                </p>

                <div>
                  <button
                    className="excel"
                    onClick={gerarRelatorioGraficoCSV}
                    title="Exportar para Excel"
                  >
                    <FaFileExcel size={22} color={colors.IC2} />
                    <div>
                      <h3>EXPORTAR</h3>
                      <h2>EXCEL</h2>
                    </div>
                  </button>
                  <button
                    className="pdf"
                    onClick={gerarRelatoriosGraficoPDF}
                    title="Exportar para PDF"
                  >
                    <FaFilePdf size={22} color={colors.IC2} />
                    <div>
                      <h3>EXPORTAR</h3>
                      <h2>PDF</h2>
                    </div>
                  </button>
                </div>
              </header>

              <main>
                <div>
                  <Grafico anos={anos} />
                </div>
              </main>

              <footer>
                {anosVisibilidade ? (
                  <Grid>
                    <GridLine>
                      <GridHeadItem title="Periodo" />
                      <GridHeadItem title="Pedidos" width={150} />
                      <GridHeadItem title="Ticket Médido" width={150} />
                      <GridHeadItem title="Produtos" width={150} />
                      <GridHeadItem title="Frete" width={150} />
                      <GridHeadItem title="Desconto" width={150} />
                      <GridHeadItem title="Total" width={150} />
                    </GridLine>

                    {anos.map((ano, key) => (
                      <GridLine key={ano.periodo}>
                        <GridItem>{ano.ano}</GridItem>
                        <GridItem width={150}>
                          {exibicaoTotalPedidos(ano.pedidos)}
                        </GridItem>
                        <GridItem width={150}>
                          {ano.ticketmedio.toLocaleString('pt-br', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </GridItem>
                        <GridItem width={150}>
                          {ano.produtos.toLocaleString('pt-br', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </GridItem>
                        <GridItem width={150}>
                          {ano.frete.toLocaleString('pt-br', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </GridItem>
                        <GridItem width={150}>
                          {ano.desconto.toLocaleString('pt-br', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </GridItem>
                        <GridItem width={150}>
                          {ano.total.toLocaleString('pt-br', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </GridItem>
                      </GridLine>
                    ))}
                  </Grid>
                ) : null}
                <ToogleInfo
                  onClick={() => setGraficoVisibilidade(!anosVisibilidade)}
                >
                  {anosVisibilidade ? (
                    <IoMdArrowDropup size={32} color={colors.IC1} />
                  ) : (
                    <IoMdArrowDropdown size={32} color={colors.IC1} />
                  )}
                </ToogleInfo>
              </footer>
            </GraphArea>
          ) : null}
        </Container>
      </Scroll>
    </>
  );
};

export default VendasAnuais;
